/**
 * Adapter: Atlas field API → nedi-ui-web `<nedi-field>`.
 * Only CE contract attrs are forwarded (avoids jsdom Unexpected attributes).
 * Value is synced via the host `.value` property; input uses a native
 * listener (React `onInput` on formAssociated CEs is unreliable in jsdom).
 */
import { createElement, useEffect, useRef } from 'react'

export default function AvField({
  label = '',
  name,
  type = 'text',
  value = '',
  placeholder = '',
  hint = '',
  required = false,
  disabled = false,
  className = '',
  autoComplete,
  minLength,
  onChange,
}) {
  const ref = useRef(null)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const stringValue = value == null ? '' : String(value)

  useEffect(() => {
    const el = ref.current
    if (!el) return undefined
    const handler = () => {
      onChangeRef.current?.(el.value ?? '')
    }
    el.addEventListener('input', handler)
    el.addEventListener('change', handler)
    return () => {
      el.removeEventListener('input', handler)
      el.removeEventListener('change', handler)
    }
  }, [])

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (el.value !== stringValue) el.value = stringValue
  }, [stringValue])

  useEffect(() => {
    const input = ref.current?.shadowRoot?.querySelector('input')
    if (!input) return
    if (autoComplete != null) input.setAttribute('autocomplete', autoComplete)
    if (minLength != null) input.minLength = Number(minLength)
  }, [autoComplete, minLength, type, label])

  const attrs = {
    ref,
    label,
    type,
    class: ['av-nedi-field', className].filter(Boolean).join(' '),
  }
  if (name) attrs.name = name
  if (placeholder) attrs.placeholder = placeholder
  if (hint) attrs.hint = hint
  if (required) attrs.required = ''
  if (disabled) attrs.disabled = ''

  return createElement('nedi-field', attrs)
}
